import * as THREE from 'three';

import { CORNERS, elevationToY } from './HexMetrics';
import { axialToWorld } from './HexCoordinates';
import { HexCellData, cornerElevation, groundLevel } from './HexSurface';

/**
 * Rivers, drawn as a ribbon over the ground rather than by colouring the cell.
 *
 * A river runs *through* part of a hex and leaves the rest as whatever grows on
 * its banks — so it cannot be a biome without throwing that away. It is a
 * separate mesh laid on the terrain, and the cell underneath keeps its colour.
 *
 * The generator sends one byte per cell: bits 0–5 say which edges the water
 * crosses, bits 6–7 how big the river is. Both have to be data. Inferring the
 * edges here from which neighbours happen to be rivers gets confluences wrong,
 * and the width is the flow accumulation, which only the whole map knows.
 *
 * # Shape
 *
 * A cell the river merely passes through — two arms, which is nearly all of them
 * — is drawn as a **curve**, not as two straight arms meeting at the centre. The
 * curve is a quadratic Bézier from one edge midpoint to the other **with the
 * cell centre as its control point**, and that one choice does everything:
 *
 * - its tangent at each end is exactly the centre-to-midpoint direction, which
 *   is also the direction the neighbour's arm leaves by, because the line
 *   between two adjacent centres crosses their shared edge at right angles. So
 *   the whole river is smooth **across cells** without any cell knowing anything
 *   about its neighbours;
 * - a run straight through opposite edges collapses to a straight line through
 *   the centre, so nothing is bent that should not be;
 * - there is no tuning constant to get wrong.
 *
 * Springs, mouths and confluences keep the polygon below: one arm has nothing to
 * curve towards, and three or more do not lie on a single curve at all. Both
 * still end flush at the edge midpoints, so they join the curved runs exactly.
 *
 * Anywhere the polygon is used, one arm per connected edge runs from the cell
 * centre out to the middle of that edge at constant width — and the arms of a
 * cell form **one polygon**, not a pile of overlapping rectangles.
 *
 * Where two arms meet, the polygon's corner is the point where their outer
 * sides actually cross: the join is mitred, the way a stroked polyline is. That
 * is what makes a bend read as a bend rather than as two strips laid over each
 * other with a notch on the outside and a bulge on the inside.
 *
 * Two earlier attempts and why they were not enough:
 *
 * - A hexagonal patch at the cell centre. Its edges matched the arms exactly,
 *   but a regular hexagon whose side is the ribbon's width is `√3` times wider
 *   across the flats, so every centre wore a visible knot.
 * - Independent rectangles left to overlap. No knot, but every bend was a right
 *   angle cut across, and a confluence was three strips crossing rather than a
 *   fork.
 *
 * # Staying on the surface
 *
 * The terrain is a fan of triangles from the cell centre to its six corners, so
 * the height at any point inside the cell is decided by whichever of those six
 * the point falls in. `surfaceHeight` finds it and interpolates, which puts
 * every vertex exactly on the ground however steep it is — no sampling grid, no
 * guessing, and no gap on a slope.
 */

/**
 * Half-width of the ribbon in world units, by the generator's width class.
 *
 * Exported because the props have to stand clear of the water they are next to,
 * and a second copy of these four numbers would drift the day one of them
 * changes — leaving boulders in the river on a map that looks fine.
 */
export const HALF_WIDTHS = [0.035, 0.06, 0.095, 0.14];

/** Bits 0–5 of the river byte. */
export const EDGE_MASK = 0b0011_1111;

/**
 * Lift off the ground, in world units.
 *
 * Small enough to be invisible, large enough that the ribbon wins the depth
 * test on gentle ground. Steep ground is handled by the polygon offset on the
 * material, which works in screen space and does not care about the slope.
 */
const LIFT = 0.01;

/**
 * How many pieces a curved run through one cell is cut into.
 *
 * The same eight the route line uses, and for the same reason: it is where the
 * corner stops being visible at the zoom this game is played at. Only cells with
 * exactly two arms are curved, and those are almost all of them, so this is what
 * the ribbon costs.
 */
const SEGMENTS = 8;

export function createRiverMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: 0x3d84c6,
    roughness: 0.35,
    metalness: 0,
    // The ribbon shares its plane with the terrain under it. Without this the
    // two fight over every pixel and the river flickers as the camera moves.
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
}

/**
 * Height of the terrain at a point inside a cell, in world units.
 *
 * The point is located in one of the six fan triangles and interpolated across
 * it. Anything outside them all — which only rounding can produce — falls back
 * to the cell's own height, so a vertex is never left at zero.
 */
function surfaceHeight(
  cell: HexCellData,
  world: Map<number, HexCellData>,
  offsetX: number,
  offsetZ: number
): number {
  const centerY = elevationToY(groundLevel(cell));

  for (let corner = 0; corner < 6; corner++) {
    const a = CORNERS[corner];
    const b = CORNERS[corner + 1];

    // Barycentric coordinates against (centre, a, b), all taken as offsets from
    // the centre so the centre's own coordinates are zero.
    const determinant = a.x * b.z - b.x * a.z;
    if (Math.abs(determinant) < 1e-12) continue;

    const u = (offsetX * b.z - b.x * offsetZ) / determinant;
    const v = (a.x * offsetZ - offsetX * a.z) / determinant;
    if (u < -1e-9 || v < -1e-9 || u + v > 1 + 1e-9) continue;

    const aY = elevationToY(cornerElevation(world, cell, corner));
    const bY = elevationToY(cornerElevation(world, cell, (corner + 1) % 6));
    return centerY * (1 - u - v) + aY * u + bY * v;
  }

  return centerY;
}

/**
 * One ribbon mesh for a chunk, or null when no cell in it carries a river.
 *
 * `world` rather than just the members, for the same reason the terrain needs
 * it: a corner belongs to three cells and its height is the mean over them, so
 * a ribbon reaching a chunk border has to agree with the one on the other side.
 */
interface Arm {
  /** Middle of the edge this arm ends on, as an offset from the cell centre. */
  midX: number;
  midZ: number;
  /** Unit vector from the centre out along the arm. */
  outX: number;
  outZ: number;
  /** Half the ribbon width, across the arm, pointing from corner d to d + 1. */
  acrossX: number;
  acrossZ: number;
}

/**
 * Where the sides of two arms cross.
 *
 * `before` contributes the side facing forwards, `after` the side facing back,
 * and their intersection is the outer corner of the join. Two arms that leave
 * the cell in opposite directions have parallel sides and no crossing — that is
 * a straight run, and the answer is simply the side itself.
 */
function miter(before: Arm, after: Arm): { x: number; z: number } {
  const px = before.acrossX;
  const pz = before.acrossZ;
  const qx = -after.acrossX;
  const qz = -after.acrossZ;

  // Solving `p + t·before = q + s·after` by crossing both sides with `after`,
  // which leaves t alone. The planar cross product here is `a.x·b.z − a.z·b.x`
  // and it has to be taken in that order on both sides — reversing it in the
  // denominator alone mirrors the corner through the cell centre, which folds
  // the outline back on itself and turns some of its triangles inside out.
  const denominator = before.outX * after.outZ - before.outZ * after.outX;
  if (Math.abs(denominator) < 1e-9) {
    return { x: px, z: pz };
  }

  const t = ((qx - px) * after.outZ - (qz - pz) * after.outX) / denominator;
  return { x: px + before.outX * t, z: pz + before.outZ * t };
}

/**
 * Co pole niesie: przez które krawędzie wstęga przechodzi i jak jest szeroka.
 *
 * `null` na polu, przez które nic nie idzie.
 */
export type Ribbon = { edges: number; halfWidth: number } | null;

/**
 * Wstęga z maski krawędzi, dla dowolnej rzeczy, która przez pole **przechodzi**.
 *
 * Wyciągnięte z rzeki, gdy drogi okazały się tym samym pytaniem: przez które
 * krawędzie i jak szeroko. Wszystko poniżej — łuki, zszywanie ramion, wysokość
 * czytana z powierzchni terenu — jest o wstędze, a nie o wodzie; jedyne, co
 * rzeka wnosiła, to skąd wziąć maskę i szerokość. Druga kopia tej geometrii
 * byłaby bliźniaczą regułą, o której ten projekt pisze przy ruchu — tyle że
 * wziętą na siebie bez powodu, bo tu obie strony są w jednym pliku.
 */
export function buildRibbonGeometry(
  members: HexCellData[],
  world: Map<number, HexCellData>,
  read: (cell: HexCellData) => Ribbon
): THREE.BufferGeometry | null {
  const positions: number[] = [];
  const indices: number[] = [];

  for (const cell of members) {
    const ribbon = read(cell);
    if (!ribbon) continue;

    const { edges, halfWidth } = ribbon;
    const { x, z } = axialToWorld(cell.q, cell.r);

    // Everything is built as an offset from the cell centre, because that is
    // what `surfaceHeight` needs and it keeps the arithmetic in one frame.
    const push = (offsetX: number, offsetZ: number) => {
      const index = positions.length / 3;
      positions.push(
        x + offsetX,
        surfaceHeight(cell, world, offsetX, offsetZ) + LIFT,
        z + offsetZ
      );
      return index;
    };

    // Arms in direction order, which is the order they sit round the cell — so
    // consecutive entries are the pairs whose sides have to be mitred together.
    const arms: Arm[] = [];
    for (let direction = 0; direction < 6; direction++) {
      if ((edges & (1 << direction)) === 0) continue;

      // Edge `direction` runs between corners `direction` and `direction + 1`,
      // matching how the terrain fan is wound.
      const left = CORNERS[direction];
      const right = CORNERS[direction + 1];
      const midX = (left.x + right.x) / 2;
      const midZ = (left.z + right.z) / 2;

      // Across the arm: along the edge it ends on. Keeping the mouth flat
      // against the cell boundary is what makes it flush with the neighbour's.
      const alongX = (right.x - left.x) / 2;
      const alongZ = (right.z - left.z) / 2;
      const alongLength = Math.hypot(alongX, alongZ) || 1;
      const outLength = Math.hypot(midX, midZ) || 1;

      arms.push({
        midX,
        midZ,
        outX: midX / outLength,
        outZ: midZ / outLength,
        acrossX: (alongX / alongLength) * halfWidth,
        acrossZ: (alongZ / alongLength) * halfWidth,
      });
    }

    // Two arms is a river passing through, and a curve is what that is. Drawn
    // as a strip along the Bézier rather than as part of the outline below,
    // because a strip is what a curve of constant width is — the polygon is for
    // the places where arms genuinely meet.
    if (arms.length === 2) {
      const [from, to] = arms;
      let leftBefore = -1;
      let rightBefore = -1;

      for (let step = 0; step <= SEGMENTS; step++) {
        const t = step / SEGMENTS;
        const s = 1 - t;

        // The control point is the cell centre, which is the origin of these
        // offsets — so its term drops out of the position entirely.
        const pointX = s * s * from.midX + t * t * to.midX;
        const pointZ = s * s * from.midZ + t * t * to.midZ;

        // B'(t) = 2·(1−t)·(C − P₀) + 2·t·(P₂ − C), with C at the origin.
        let tangentX = 2 * (t * to.midX - s * from.midX);
        let tangentZ = 2 * (t * to.midZ - s * from.midZ);
        const length = Math.hypot(tangentX, tangentZ) || 1;
        tangentX /= length;
        tangentZ /= length;

        // Across the ribbon. At either end the tangent is the arm direction, so
        // this comes out along the cell edge on its own — the mouth stays flat
        // against the boundary and flush with the neighbour's, which is the
        // whole reason the control point is where it is.
        const acrossX = -tangentZ * halfWidth;
        const acrossZ = tangentX * halfWidth;

        const left = push(pointX + acrossX, pointZ + acrossZ);
        const right = push(pointX - acrossX, pointZ - acrossZ);

        if (step > 0) {
          indices.push(leftBefore, left, rightBefore);
          indices.push(rightBefore, left, right);
        }

        leftBefore = left;
        rightBefore = right;
      }
      continue;
    }

    // The outline, walked once: for each arm, the corner it shares with the
    // previous one, then the two points where it meets the cell edge. A single
    // arm has nothing to share with, so it ends square at the centre — a
    // spring, or a mouth where the sea takes over.
    const outline: { x: number; z: number }[] = [];
    for (let index = 0; index < arms.length; index++) {
      const arm = arms[index];

      if (arms.length === 1) {
        outline.push({ x: -arm.acrossX, z: -arm.acrossZ });
      } else {
        const previous = arms[(index + arms.length - 1) % arms.length];
        outline.push(miter(previous, arm));
      }

      outline.push({ x: arm.midX - arm.acrossX, z: arm.midZ - arm.acrossZ });
      outline.push({ x: arm.midX + arm.acrossX, z: arm.midZ + arm.acrossZ });

      if (arms.length === 1) {
        outline.push({ x: arm.acrossX, z: arm.acrossZ });
      }
    }

    if (arms.length === 1) {
      // A quad, not a fan: the cell centre lies on the outline itself here, so
      // fanning from it would emit one triangle of zero area for every spring
      // and mouth in the world.
      const ring = outline.map((point) => push(point.x, point.z));
      indices.push(ring[0], ring[1], ring[2], ring[0], ring[2], ring[3]);
      continue;
    }

    // Fanned from the cell centre. The outline is star-shaped around it — every
    // point of it is reached by going outwards — so a fan is a valid
    // triangulation and needs no general polygon splitting.
    const middle = push(0, 0);
    const ring = outline.map((point) => push(point.x, point.z));
    for (let index = 0; index < ring.length; index++) {
      indices.push(middle, ring[index], ring[(index + 1) % ring.length]);
    }
  }

  if (indices.length === 0) return null;

  const geometry = new THREE.BufferGeometry();
  geometry.setIndex(indices);
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));

  // Every normal points straight up, and none is computed from the geometry.
  //
  // A ribbon's vertices are not shared between cells — two neighbours meeting
  // at an edge have different widths whenever they are different width classes,
  // so there is no common point to share — and averaging faces within one cell
  // therefore lights each cell's piece on its own. Measured on real terrain:
  // coincident vertices of neighbouring cells disagreed by **17,2° on average
  // and up to 67,8°**, with 47% of them over 15°, which is a river drawn as a
  // chain of separately shaded segments.
  //
  // Flat-up is not a fudge around that: a river is a film of water lying on the
  // ground, and what it reflects is the sky. Lighting it as though it were
  // horizontal is what makes it read as water rather than as folded metal —
  // and it is the same thing the sea's flat sheet does for free.
  const upward = new Float32Array(positions.length);
  for (let i = 1; i < upward.length; i += 3) {
    upward[i] = 1;
  }
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(upward, 3));

  return geometry;
}

/**
 * One ribbon mesh of river for a chunk, or null when no cell in it carries one.
 *
 * The river's own reading of the shared builder: bits 0–5 say which edges the
 * water crosses and bits 6–7 how big it is, and both come off the layer.
 */
export function buildRiverGeometry(
  members: HexCellData[],
  world: Map<number, HexCellData>
): THREE.BufferGeometry | null {
  return buildRibbonGeometry(members, world, (cell) => {
    const byte = cell.river ?? 0;
    const edges = byte & EDGE_MASK;
    if (edges === 0) return null;
    return { edges, halfWidth: HALF_WIDTHS[(byte >> 6) & 0b11] };
  });
}
